import {
  ApiError,
  CHAT_MAX_CHARS,
  connectLobby,
  deployLobby,
  fetchScenarios,
  openLobby,
  setDefaultCamo,
  startDaily,
  type ChatLine,
  type LobbySocket,
  type MatchPlayer,
  type Scenario,
  type ScenarioSlot,
  type ScenarioUnit,
  type Session,
  type Watcher,
} from "../api";
import { showAvatar } from "../avatars";
import { footer } from "../chrome";
import { el, onTeardown, render } from "../dom";
import { withHandleTypeahead } from "../handle-typeahead";
import { nav } from "../nav";
import {
  camoModal,
  camoPicker,
  pickedBlob,
  pickedPixels,
  type Picker,
} from "./camo-picker";
import { pilotHover, type PilotFacts } from "./pilot-hover";
import {
  challengeBySlug,
  dailyScenario,
  flatCamo,
  scenarioBrief,
  sideRanks,
  type Challenge,
} from "./daily";
import { sideCss, sideRgb } from "../content/sides";
import { presetNamed } from "../content/megamek-text";
import { costText, forceTotals, statsFor, tonsText } from "../content/units";
import { scenarioPool, type ChallengeMode } from "./scenario-pool";
import { notReadyNote, soonStamp } from "../soon-stamp";
import { waitingScreen } from "./waiting";

export type { ChallengeMode } from "./scenario-pool";

/**
 * The teams a lobby may put a force on, mirroring the API's own `TEAMS`.
 *
 * One per seat at the widest, which is what a free-for-all is, and eight is
 * where the seat count stops. There is no team 0: "on no team" is not a
 * thing a scenario slot can be, and handing the question back to the file
 * is a separate choice in the picker.
 */
const TEAM_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8];

/**
 * Who takes what, in a daily.
 *
 * Named rather than counted when there are two, because "the bot takes OPFOR"
 * is the useful sentence. Counted past that: Bug Company fields three pirate
 * lances, and listing them all says less than saying there are three.
 */
function dailySeatNote(daily: Challenge): string {
  const mine = daily.sides[0]?.name ?? "";
  const theirs = daily.sides.slice(1);
  if (theirs.length === 1) {
    return `You take ${mine}; the bot takes ${theirs[0]?.name ?? ""}. Both are fixed.`;
  }
  return `You take ${mine}; the bot takes the other ${theirs.length} forces. All are fixed.`;
}

/** "@handle", the same convention challenge.ts's tiles use. */
function at(handle: string): string {
  return `@${handle}`;
}

/**
 * How a visit to this screen begins: opening a fresh lobby with no id yet
 * (matches.ts's Duel/Scenario cards), or joining one that already exists by
 * its id (a shared link, resolved by router.ts's "#lobby/<id>" route, or a
 * signed-in player's own reload of a lobby they already opened).
 *
 * The two paths differ in exactly one thing worth calling out: `mode` is
 * only ever the fresh opener's own choice — Duel or Scenario narrows the
 * picker's pool for someone about to pick a scenario for the first time —
 * and it is not information a shared link carries or needs, since a joiner
 * is joining whatever scenario is already live there, not choosing an
 * intent. A joiner's picker still needs a pool to render from if it stays
 * interactive, so it gets the whole, unnarrowed catalog instead.
 *
 * The third is the daily challenge, which is this screen with almost
 * everything about it already decided — see the daily paragraph of
 * lobbyScreen's own doc below. It carries the slug rather than the challenge
 * itself because the slug is what survives a sign-in round trip, and turning
 * one into the other is a lookup in a file this screen already has.
 */
export type LobbyEntry =
  /**
   * A lobby nobody has opened yet. `invite` is who to seat opposite once
   * there are seats to seat them in - set when the player pressed Challenge
   * on somebody's page, and unset for the Duel and Scenario cards, which are
   * an invitation to choose.
   */
  | { kind: "fresh"; mode: ChallengeMode; invite?: string }
  | { kind: "join"; matchId: string }
  | { kind: "daily"; slug: string };

/**
 * Pick a scenario together, claim seats one at a time, and Deploy once
 * every slot is filled.
 *
 * Reachable from `matches.ts`'s Duel and Scenario cards, which used to open
 * `challengeScreen` directly and now open this instead — see its own module
 * doc for why that is a real product change, not a rename. `challengeScreen`
 * itself is untouched and still in the codebase; nothing here imports from
 * it except `./camo-picker`, the one piece of match setup the two screens
 * share outright — see that module's own doc for why this is the one
 * genuine share, not the "duplicate the list rendering rather than force it
 * to share" call this screen's first slice already made (see `at()` and the
 * seat tile below).
 *
 * `entry.mode` narrows the scenario pool the same way it does on
 * `challengeScreen`, for a fresh open only — `scenarioPool(catalog, mode)`
 * is the other thing shared between the two screens, and the only thing that
 * ever differed between Duel and Scenario on either of them. It is not
 * information a shared link carries or needs: a joiner is joining whatever
 * scenario is already live there, not choosing an intent.
 *
 * What is here: an invite link to bring someone else straight into this
 * lobby, a live-synced scenario picker, and one Force card per slot of the
 * current scenario — the force's name, a team indicator (stubbed, see
 * `forceCard`'s own doc: the catalog has no alliance data), the seat itself
 * (claim it yourself, hand it to the bot, or invite a specific player by
 * handle — who is seated the moment their handle resolves, with no accept
 * step; see plan/invitations.md for why that is a gap this shares with the
 * rest of the site, not a decision made here — plus a Kick action once a
 * seat is claimed, always shown, since this client cannot reliably know in
 * advance whether the current session is this lobby's owner, the only
 * identity the API actually lets kick anyone; see `seatHolder`'s own doc,
 * and Deploy's identical "show it, let the server decide" posture below),
 * and a bulleted list of the force's units, each a small painted icon (the
 * same compositor `scenario/preview.ts` uses for its own hexes, at icon
 * size — camo'd if the force is yours and you have picked one, bare sprite
 * grey otherwise) with a stubbed pilot label (the catalog has no pilot data
 * either). Below the cards: your own camo — a picker identical to
 * challenge.ts's, except a user-initiated choice here writes the account's
 * stored default rather than riding along with a launch request, since a
 * claimed seat has no launch-time camo field of its own to carry one on (see
 * camo-picker.ts's module doc) — and a Deploy button enabled once every slot
 * is claimed and every other seated player has pressed Ready.
 *
 * Everything about the other people is one floating panel instead
 * (`chatPanel`, `.lobby-chat`): chat — anyone connected may talk, not just a
 * seated player, since a lobby is where seats get arranged — the link that
 * brings somebody into this lobby, and Ready. It is fixed rather than a
 * block at the bottom of the card because a four-force scenario is taller
 * than the viewport, and a player waiting on somebody else needs the
 * conversation in front of them the whole time; a narrow viewport puts it
 * back in the flow, where a floating panel would cover the forces it is
 * about. Ready sits there for the same reason: the player it is for is not
 * the one deploying. Pressing Deploy is the deployer's own readiness, so
 * they are never waited on for a second press, and any seat or scenario
 * change drops every readiness in the lobby, since what was agreed to is no
 * longer what is on screen. What is deliberately not here yet, see
 * plan/lobby.md: a seat-holder releasing their *own* seat (Kick is the owner
 * acting on someone else's, a different and narrower gap than
 * self-release), and any moderation on chat (no rate limit, no mute, no
 * report path).
 *
 * Exactly one `openLobby` call happens per visit to this screen, and only on
 * the fresh path — a join connects straight to the existing id instead, or
 * it would mint a second, unrelated lobby row. Every live update after
 * either path connects — scenario, seats, chat, Deploy becoming clickable —
 * mutates the existing DOM in place rather than calling the app's top-level
 * `render()` again. `render()` tears the current screen down through
 * `onTeardown`, which is what closes this screen's socket; calling it
 * mid-visit would close the real socket and, on the fresh path, risk
 * running `openLobby` a second time. The only legitimate calls to
 * `render()` from in here are the one-time move to `waitingScreen` once
 * `deployed` fires — Back leaves through `nav.start!()` instead, since this
 * screen now has a real address of its own to fix on the way out (see the
 * Back handler's own comment).
 *
 * ## The daily challenge
 *
 * `{ kind: "daily" }` is this same screen with everything a lobby exists to
 * negotiate already settled, and it narrows rather than forks: one screen
 * that knows how to show a scenario, its forces and your camo, with the
 * pieces that have nothing to decide taken out. What it keeps is what a
 * player still wants — the scenario, both forces with their units painted,
 * and their own camo on the force they are taking.
 *
 * What it drops, and why each one has nothing to do here: the scenario
 * picker (the day's fight is the point of the day's fight), both seat
 * resolvers and Kick (the player takes the challenger's side and the bot
 * takes the other, every day, for everybody — a swap would be a different
 * fight to the same score), the invite link, and chat, which is the plain
 * one: it is one player against a bot and there is nobody on the other end.
 *
 * It also opens no lobby and connects no socket. There is no id to share and
 * no state for two clients to agree on, and `POST /api/daily/<slug>` is a
 * single call that claims the attempt and launches the match — see `launch()`
 * below, which is the one thing Deploy does differently here.
 *
 * The scenario itself does not come from `/api/scenarios`: a daily is
 * compiled into the API and carried in the launch manifest, so `daily.ts`'s
 * bundled copy is what this screen draws (`dailyScenario`), and the two
 * forces paint in that card's own two colours (`SIDE_COLOURS`) rather than in
 * a second list that could disagree with it.
 */
export function lobbyScreen(session: Session, entry: LobbyEntry): Node[] {
  /**
   * The day's fight, when this is a daily. Null on every other path, and null
   * for a slug this build's `daily.json` has never heard of — a stash left by
   * a much older visit, which the note below says so rather than pretending
   * to set up.
   */
  const daily: Challenge | null =
    entry.kind === "daily" ? challengeBySlug(entry.slug) : null;
  /**
   * Who the player pressed Challenge on, until they have been seated.
   *
   * Spent on the first seat snapshot that has room for them and never read
   * again — a seat this arranges is an arrangement anybody in the lobby may
   * then change, and a pending invite that fired twice would put them back
   * after somebody kicked them.
   */
  let toSeat = entry.kind === "fresh" ? (entry.invite ?? null) : null;
  const status = el("p", { className: "error", role: "alert" });
  const scenarioNote = el("p", {
    className: "hint",
    textContent: entry.kind === "join" ? "Connecting…" : "Opening a lobby…",
  });
  const scenarioList = el("div", { className: "scenario-list" });
  const seatNote = el("p", {
    className: "hint",
    textContent: daily
      ? dailySeatNote(daily)
      : "Any slot you leave alone stays open for someone to claim.",
  });
  const seatList = el("div", { className: "slot-list" });
  const deploy = el("button", {
    type: "button",
    textContent: "Deploy",
    disabled: true,
  });
  // An append-only log: the backlog on connect, then whatever is said after
  // — see api.ts's LobbyCallbacks.onChat for why arrival order alone is
  // already the right order, backlog included.
  const chatScroll = el("div", { className: "chat-scroll", role: "log" });
  chatScroll.setAttribute("aria-live", "polite");
  const chatInput = el("input", {
    type: "text",
    placeholder: "Say something…",
    maxLength: CHAT_MAX_CHARS,
    autocomplete: "off",
  });
  chatInput.setAttribute("aria-label", "Chat");
  // A toggle, the same shape the camo collection's "Make default" uses: the
  // label says what the state is, `.active` and aria-pressed say it again to
  // a stylesheet and a screen reader, and pressing it again takes it back.
  // Beside chat because that is where a player waiting for everyone else is
  // already looking, and because being ready is a thing you say to the room.
  const readyToggle = el("button", {
    type: "button",
    className: "secondary small-btn lobby-ready",
    hidden: true,
    title: "Say you will fight the lineup as it stands",
  });
  const readyNote = el("p", { className: "hint", role: "status" });
  // Who is in the room, above what is being said in it. A lobby has
  // spectators by construction — connecting needs only a session — so this
  // is not the seat map with a different coat of paint: it is the people
  // arguing about who takes which force, some of whom hold no seat at all.
  // One card for the whole screen, pointed at whichever name is under the
  // cursor or the focus ring — see pilot-hover.ts for why it is one and not
  // one per name.
  const hover = pilotHover();
  const rosterList = el("div", { className: "lobby-roster" });
  const rosterCount = el("span", { className: "hint lobby-roster-count" });

  // Disabled until matchId is known: a fresh open has no id to link to until
  // opening the lobby resolves, and copying a link nobody can follow yet is
  // worse than a moment of nothing. A join already has its id from the
  // address that brought it here, so this is enabled immediately on that
  // path. It lives in the chat panel below, beside the people it is for.
  const copyLink = el("button", {
    type: "button",
    className: "secondary small-btn",
    textContent: "Copy invite link",
    disabled: true,
  });
  const copyStatus = el("span", { className: "hint", role: "status" });
  copyLink.addEventListener("click", () => {
    const id = matchId;
    if (!id) return;
    const url = `${window.location.origin}/#lobby/${encodeURIComponent(id)}`;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        copyStatus.textContent = "Copied.";
      })
      .catch(() => {
        // No clipboard permission, or none of a secure context to grant it
        // in: the URL itself, shown in the open, is still something a
        // player can select and copy by hand.
        copyStatus.textContent = url;
      });
  });

  /** The pool this mode offers, narrowed from the catalog once it arrives. */
  let pool: Scenario[] = [];
  /** The lobby's current scenario — set once opening resolves, and again on
   * every `scenario` event, including the one this socket's own change
   * echoes back. */
  let current: Scenario | null = null;
  /** The lobby's current seat map, in whatever order the server sent it. */
  let seats: MatchPlayer[] = [];
  /**
   * Everyone who has pressed Ready, by DID, as the API last said. Emptied by
   * every seat and scenario change — the API sends that empty snapshot
   * itself, so this only ever mirrors it rather than guessing when to clear.
   */
  let readyDids: string[] = [];
  /**
   * Whose lobby this is, once the socket has said. Null before that, and on
   * the daily path, which has no lobby and no socket — `otherPlayers` reads
   * it as "this session", which is what a fresh open and a daily both are.
   */
  let ownerDid: string | null = null;
  /**
   * Everyone with a socket open, as the API last said. Null before the
   * first snapshot, which is what keeps a fresh connection from announcing
   * the whole room as having "joined" — see `renderRoster`.
   */
  let here: Watcher[] | null = null;
  /**
   * The slots the host is holding open for somebody, as the API last said.
   * Read-only here: nothing on this screen locks anything any more, and a
   * lock another client set is why an open force can refuse "You" and "AI"
   * — see `seatActions`.
   */
  let lockedSlots: string[] = [];
  /**
   * The open forces whose Invite field is showing, and what has been typed
   * into each. Out here rather than on the card because every seat snapshot
   * rebuilds the cards - see `seatActions`.
   */
  const inviting = new Map<string, string>();
  let socket: LobbySocket | null = null;
  let matchId: string | null = null;
  // Set the moment this screen is torn down, so a `close` event the
  // teardown's own socket.close() causes does not get mistaken for the
  // connection dying out from under a screen that is still up — see
  // onClosed below.
  let left = false;

  function scenarioByPath(path: string): Scenario | null {
    return pool.find((option) => option.path === path) ?? null;
  }

  function renderScenarioList(): void {
    scenarioList.replaceChildren();
    if (daily) {
      // Named, not offered, and shown the way the front page shows it: there
      // is one fight today, picking another would make it a different
      // challenge to the same score, and what a player wants here is to look
      // at the one they came for.
      scenarioList.append(scenarioBrief(daily.name, daily.blurb, daily));
      return;
    }
    // The one that is actually loaded, in full, above the list of the rest.
    // A catalog scenario has no board, no zones and no battle values, so the
    // brief is its name and its description - but it is the same rendering,
    // and it fills in the moment anything knows more.
    if (current) {
      scenarioList.append(
        scenarioBrief(current.name, current.description, null),
      );
    }
    for (const option of pool) {
      const input = el("input", {
        type: "radio",
        name: "scenario",
        value: option.path,
        checked: current?.path === option.path,
      });
      input.addEventListener("change", () => socket?.setScenario(option.path));
      scenarioList.append(
        el("label", { className: "choice" }, [
          input,
          el("div", {}, [
            el("strong", { textContent: option.name }),
            el("p", { className: "hint", textContent: option.description }),
          ]),
        ]),
      );
    }
  }

  // ----- force cards: painted units, not a plain-text list -----------------
  //
  // The unit icons reuse `scenario/preview.ts`'s compositor
  // (`paintUnitIcon`/`chassisOf`) rather than a second sprite-and-camo
  // implementation — it is the camo editor's own port of MegaMek's
  // compositor and MegaMek's real sprites, so what a card draws is what the
  // match will actually look like. Loaded lazily and never fatally, the
  // same as the camo strip: this is the heaviest thing on the screen and
  // nothing here is worth refusing to open a lobby over. A card built
  // before the chunk lands falls back to the bare unit designation and an
  // undrawn icon; the load's own `.then` re-renders the cards once it is
  // ready.
  let previewModule: typeof import("../scenario/preview") | null = null;
  void import("../scenario/preview")
    .then((mod) => {
      previewModule = mod;
      renderSeats();
    })
    .catch((error: unknown) => {
      console.warn("lobby: the scenario preview could not load", error);
    });

  /** Bumped once per renderSeats() call, so a unit icon that finishes
   * painting after a newer render started (a fast scenario or seat change)
   * never draws onto a canvas that has already been replaced. */
  let renderSeq = 0;

  /**
   * A picker per bot force, kept by slot so a re-render does not throw away
   * what the force is wearing.
   *
   * Chosen here rather than server-side and held rather than re-rolled at
   * launch, for the same reason challenge.ts holds its own: the player is
   * shown what the opposition will be wearing, and a second roll on the way
   * out would make that a lie.
   *
   * A bot's camo used to be a roll and a Shuffle button, which is not a
   * choice - the player could ask for a different one but never for a
   * particular one. It is the same picker their own force uses now, opening
   * on a roll rather than on their marked default: a force with no account
   * has no default of its own. A daily's opposition opens on None instead,
   * which is the flat side colour that screen has always drawn.
   */
  const botCamo = new Map<
    string,
    { picker: Picker; button: HTMLButtonElement; dialog: HTMLDialogElement }
  >();

  function botCamoFor(slotName: string) {
    let held = botCamo.get(slotName);
    if (!held) {
      const picker = camoPicker(session, daily ? "none" : "random", false);
      picker.onChange = () => repaint.get(slotName)?.();
      const opened = camoModal(picker, `Choose ${slotName}'s camo`, "swatch");
      held = { picker, ...opened };
      botCamo.set(slotName, held);
    }
    return held;
  }

  /**
   * How to repaint one force's unit icons, keyed by slot, rebuilt by every
   * `renderSeats`.
   *
   * A camo change affects exactly one force, and rebuilding all of them for it
   * was the whole cost: Monte Diablo fields twenty-four machines across four
   * forces, so Shuffle on one bot lance re-composited every sprite on the
   * screen. Repainting in place also keeps the cards from flickering.
   */
  const repaint = new Map<string, () => void>();

  const camo = camoPicker(session);
  camo.onChange = (userInitiated) => {
    repaintMine();
    if (!userInitiated) return;
    // A rolled camo is not a record, so there is nothing to point an account
    // default at. It rides on the launch instead - see `launch` below.
    if (camo.rolled) return;
    setDefaultCamo(camo.rkey ?? null).catch((error: unknown) => {
      // Cosmetic: the picker's own selection state already reflects the
      // click, and the match is still worth setting up without the write
      // landing. Logged so a real outage is not invisible.
      console.warn("lobby: camo default could not be saved", error);
    });
  };

  // The picker as a button and a dialog, opened from inside the force the
  // player is taking. One picker, wherever it is opened from.
  const camoOpen = camoModal(camo, "Choose camo", "swatch");

  /** The camo a force's units paint in: yours, as picked, on your own
   * claimed seat; a bot's own on a seat it was handed (see `dressNewBots`);
   * none on a seat belonging to another player, whose own choice is theirs to
   * bring, not something this client has any way to know before Deploy
   * settles it. */
  async function camoForSlot(slotName: string): Promise<ImageData | null> {
    const seat = seats.find((s) => s.slot === slotName);
    // A daily is two sides before it is anything else, so neither force draws
    // bare: the player's blue and the opposition's red, off the same list the
    // card's own map paints from (daily.ts's SIDE_COLOURS). A camo the player
    // has picked still wins on the force they are taking — that is the "full
    // camo" half of what this screen is for — and the bot wears whatever the
    // player chose for it, its own colour being what None means here.
    const bot = botCamo.get(slotName);
    const chosen = bot ? await pickedPixels(bot.picker) : null;
    if (chosen) return chosen;
    if (daily) return (await ownCamo(seat)) ?? sideCamo(slotName);
    if (!seat || seat.did !== session.did) return null;
    if (bot) return sideCamo(slotName);
    return ownCamo(seat);
  }

  /**
   * A bot landing on an open slot gets its own picker, once.
   *
   * challenge.ts has always dressed every AI slot without being asked, and a
   * lobby's bot arrived in nothing at all until somebody found Shuffle. The
   * picker is built on the seat arriving rather than on the click that sent
   * it, so a force the other player hands to the bot is painted here too, and
   * it opens on a roll - see `botCamo`.
   *
   * Only a slot that nobody held before: a force that already has a picker
   * keeps it, and a bot replacing a human keeps whatever that seat was
   * wearing rather than rerolling out from under the picture.
   */
  function dressNewBots(before: MatchPlayer[], after: MatchPlayer[]): void {
    for (const seat of after) {
      if (seat.control !== "bot") continue;
      if (botCamo.has(seat.slot)) continue;
      if (before.some((held) => held.slot === seat.slot)) continue;
      botCamoFor(seat.slot);
    }
  }

  /**
   * A force's own colour as a PNG, for a bot that brought nothing.
   *
   * A lobby-assigned bot has no repository to read a camo from, and MegaMek's
   * default is close enough to white that a scenario full of them is a screen
   * of identical pale machines. This paints it in the colour its card and its
   * icons already use, so the board matches the setup screen.
   *
   * Rolled camo wins: pressing Shuffle is asking for something else.
   */
  async function soloCamo(slotName: string): Promise<Blob> {
    const { team, slot } = ranksOf(slotName);
    const [r, g, b] = sideRgb(team, slot);
    const canvas = document.createElement("canvas");
    canvas.width = 84;
    canvas.height = 72;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = `rgb(${r} ${g} ${b})`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) =>
          blob ? resolve(blob) : reject(new Error("camo did not rasterise")),
        "image/png",
      );
    });
  }

  /** The camo the player has picked, on their own seat and nowhere else.
   *
   * Through the picker's own reader, which resolves both halves of a
   * selection: the player is shown the camo the launch will actually carry,
   * a roll included - a roll is bytes and has no record key to read. */
  async function ownCamo(
    seat: MatchPlayer | undefined,
  ): Promise<ImageData | null> {
    if (!seat || seat.did !== session.did) return null;
    return pickedPixels(camo);
  }

  /** A force's flat colour, the same one that colours its card. */
  function sideCamo(slotName: string): ImageData {
    const { team, slot } = ranksOf(slotName);
    return flatCamo(sideRgb(team, slot));
  }

  /**
   * Which of the side colours a force wears: yours is blue, the opposition is
   * red, and a third or fourth faction takes the shades after it.
   *
   * "Yours" is the seat you hold rather than the first slot in the file,
   * because on an ordinary lobby the seat is yours to pick and nothing says
   * you took the first one. Until somebody claims anything there is no "yours"
   * to find, so it falls back to file order — which is what a daily is anyway,
   * where the player always takes the first side.
   *
   * A daily defers to `sideColourIndex`, which groups by MegaMek team rather
   * than by position: Bug Company fields three pirate lances that are one
   * side, and colouring them as three opponents would say the wrong thing
   * about who is fighting whom.
   */
  function ranksOf(slotName: string): { team: number; slot: number } {
    if (daily) {
      const index = daily.sides.findIndex((side) => side.name === slotName);
      return sideRanks(daily, index === -1 ? 0 : index);
    }
    const slots = current?.slots ?? [];
    const index = slots.findIndex((s) => s.name === slotName);
    if (index === -1) return { team: 0, slot: 0 };
    const mineSlot = seats.find((seat) => seat.did === session.did)?.slot;
    const keyOf = (s: ScenarioSlot, i: number): string =>
      s.team === undefined || s.team === null ? `slot:${i}` : `team:${s.team}`;
    const key = keyOf(slots[index]!, index);
    // Whichever team holds the seat you took is team 0. Before anybody has
    // claimed anything there is no "yours", so the first force stands in.
    const mineIndex = mineSlot
      ? slots.findIndex((s) => s.name === mineSlot)
      : 0;
    const mineKey = keyOf(
      slots[mineIndex] ?? slots[0]!,
      Math.max(0, mineIndex),
    );

    const seen: string[] = [];
    for (const [i, s] of slots.entries()) {
      const k = keyOf(s, i);
      if (!seen.includes(k)) seen.push(k);
    }
    const others = seen.filter((k) => k !== mineKey);
    const team = key === mineKey ? 0 : 1 + others.indexOf(key);
    const slot = slots
      .slice(0, index)
      .filter((s, i) => keyOf(s, i) === key).length;
    return { team, slot };
  }

  /**
   * What this machine is worth with the crew this fight puts in it.
   *
   * Only a daily knows: its index carries a battle value per unit, because BV
   * 2.0 multiplies by gunnery and piloting and the catalog records no crews at
   * all. `dailyScenario` builds the slots straight off `daily.sides` in order,
   * so the two line up by position — which is also the only thing that can
   * line them up when a force fields two of the same design.
   */
  function unitBv(slotName: string, unitIndex: number): number | null {
    if (daily) {
      const side = daily.sides.find((s) => s.name === slotName);
      return side?.units[unitIndex]?.bv ?? null;
    }
    // A catalog scenario carries its own, measured with the crews its file
    // names - see services/api/src/matches/scenarios.rs.
    const slot = current?.slots.find((s) => s.name === slotName);
    return slot?.units[unitIndex]?.bv ?? null;
  }

  /**
   * A claimed seat's face, name, and a Kick action — always rendered, the
   * same "show it, let the server decide" posture Deploy already has on
   * this screen: this client has no reliable way to know in advance whether
   * the current session is this lobby's owner (a fresh open trivially knows
   * — it just opened it — but a join from a shared link may or may not be),
   * so there is nothing to conditionally hide it against. A non-owner's
   * click is a silent no-op server-side (`Lobbies::kick_seat`) — the seat
   * simply stays, the same way a stale Deploy click for a non-owner would
   * 403 with nothing shown here either.
   */
  function seatHolder(seat: MatchPlayer): HTMLElement {
    const face = el("img", { className: "seat-avatar", alt: "" });
    const isBot = seat.control === "bot";
    if (isBot) {
      face.src = "/logo.svg";
    } else {
      showAvatar(face, seat.did ?? seat.handle ?? "");
    }
    const kick = el("button", {
      type: "button",
      className: "secondary small-btn",
      textContent: "Kick",
    });
    kick.addEventListener("click", () => socket?.kickSeat(seat.slot));
    // Handing the lobby over, on the seat of whoever is taking it. Shown
    // beside Kick and refused by the API for anybody but the host, the same
    // as Kick — and never on your own seat or a bot's, which are the two
    // cases the API refuses outright.
    const seatDid = seat.did;
    const makeHost =
      isBot || !seatDid || seatDid === session.did || seatDid === ownerDid
        ? null
        : el("button", {
            type: "button",
            className: "secondary small-btn",
            textContent: "Make host",
            title: "Hand the lobby to this player",
          });
    makeHost?.addEventListener("click", () => socket?.makeHost(seatDid!));
    const tile = el("span", { className: "pilot-tile-face" }, [
      face,
      el("span", { className: "pilot-tile-info" }, [
        el("span", {
          className: "pilot-tile-handle",
          textContent: isBot ? "AI" : seat.handle ? at(seat.handle) : "Claimed",
        }),
        // Which AI, not just that it is one. Two fights with a bot in them are
        // not the same fight if one of them is hunting you.
        ...(isBot
          ? [el("span", { className: "hint pilot-tile-rank", ...aiKind() })]
          : []),
      ]),
      // Nothing to kick in a daily: both seats are fixed, there is no lobby
      // for anyone else to be in, and no socket to send it on.
      ...(daily ? [] : [kick]),
      ...(daily || !makeHost ? [] : [makeHost]),
    ]);
    // A human seat is a person; a bot seat is a setting. Only one of them
    // has a card to open.
    if (!isBot && seat.did) {
      const seatDid = seat.did;
      hover.attach(tile, () => pilotFacts(seatDid, seat.handle));
    }
    return tile;
  }

  /**
   * What the bot on the other side is set to.
   *
   * A scenario that says nothing gets MegaMek's own out-of-the-box behaviour,
   * which is a real answer and is named as one rather than left blank. A daily
   * that sets the knobs is matched back to whichever of MegaMek's own named
   * presets it equals, so the card says "Striker" instead of four numbers that
   * mean nothing to a player — see content/megamek-text.ts, which is generated
   * from `mmconf/princessBehaviors.xml`.
   */
  function aiKind(): { textContent: string; title: string } {
    const knobs = daily?.overrides?.ai;
    if (!knobs?.length) {
      return {
        textContent: "Default",
        title: "MegaMek's own default Princess behaviour.",
      };
    }
    const named = presetNamed(
      Object.fromEntries(knobs.map((a) => [a.knob, a.value])),
    );
    return {
      textContent: named ?? "Custom",
      title: named
        ? `MegaMek's own "${named}" behaviour preset.`
        : knobs.map((a) => `${a.knob} ${a.value}`).join(", "),
    };
  }

  /**
   * The one question an open force asks: who pilots it.
   *
   * Four answers, in the order a lobby reaches for them — yourself, somebody
   * you name, somebody we find, the bot — as one split plate rather than the
   * three loose buttons and a permanently open text field this used to be.
   * Nothing said which of them a visitor was meant to press, and a screen
   * that is mostly a roster carried one handle input per force. The field is
   * what "Invite" opens now, and closes again.
   *
   * "Fill" is matchmaking, which does not exist. It is disabled and wears the
   * site's own coming-soon stamp, the same mark as the bar's destinations
   * with no address behind them, rather than being left off the control: what
   * fills a force nobody wants to take is the question this control asks, and
   * a missing answer is worth saying out loud. Building it is the
   * `Matchmaking` item in plan/invitations.md.
   *
   * There is no Hold. Reserving a force for somebody who may never arrive is
   * the reverse of what an open force is for — the answer to "keep this for
   * my friend" is to invite them. The API's lock is still there and still
   * broadcast, so a slot another client locked reads as held here and says
   * why the two answers the API refuses are refused; nothing in this client
   * sets one any more.
   *
   * Nothing here reports why an invite did not seat anyone — an unknown
   * handle, one already seated elsewhere in this lobby — the same silent
   * posture `claimSeat`/`assignBot` already have on this screen: the only
   * signal is whether the next seat snapshot shows them in it. Resolving a
   * handle is also a real network round trip on the API side, so that
   * snapshot can arrive well after the click that asked for it, unlike the
   * other two resolvers.
   */
  function seatActions(slotName: string): HTMLElement {
    const locked = lockedSlots.includes(slotName);

    const inviteInput = el("input", {
      type: "text",
      placeholder: "their-handle.bsky.social",
      autocapitalize: "none",
      autocomplete: "off",
      spellcheck: false,
    });
    inviteInput.setAttribute("aria-label", `Invite a player as ${slotName}`);
    const inviteButton = el("button", {
      type: "submit",
      className: "secondary small-btn",
      textContent: "Send",
    });
    const inviteForm = el("form", { className: "lineup-add" }, [
      withHandleTypeahead(inviteInput),
      inviteButton,
    ]);
    // Both halves of the field's state are in `inviting` rather than in the
    // DOM: renderSeats rebuilds every card from scratch, and a seat snapshot
    // landing while somebody is halfway through a handle must not take the
    // field, or what they had typed, away from under them.
    inviteForm.hidden = !inviting.has(slotName);
    inviteInput.value = inviting.get(slotName) ?? "";
    inviteInput.addEventListener("input", () => {
      inviting.set(slotName, inviteInput.value);
    });
    inviteForm.addEventListener("submit", (event) => {
      event.preventDefault();
      const handle = inviteInput.value.trim().replace(/^@/, "");
      if (!handle) return;
      socket?.inviteHandle(slotName, handle);
      // Cleared on send, not on a confirmed seat: there is nothing this
      // screen is ever told that would make waiting to clear it meaningful.
      // The field stays open for the same reason — nothing here knows the
      // handle resolved, and a field that vanished would read as if it had.
      inviteInput.value = "";
      inviting.set(slotName, "");
    });

    const you = seatAnswer("You", () => socket?.claimSeat(slotName));
    you.classList.add("plate-tl");
    const invite = seatAnswer("Invite", () => {
      const open = inviteForm.hidden;
      if (open) inviting.set(slotName, inviteInput.value);
      else inviting.delete(slotName);
      inviteForm.hidden = !open;
      invite.classList.toggle("active", open);
      invite.setAttribute("aria-expanded", String(open));
      if (open) inviteInput.focus();
    });
    invite.classList.toggle("active", !inviteForm.hidden);
    invite.setAttribute("aria-expanded", String(!inviteForm.hidden));
    // Disabled and stamped, not hidden: see this function's own doc. The
    // label carries the note a stamp cannot give a screen reader, the same
    // pairing the bar's three destinations use.
    const fill = el("button", {
      type: "button",
      className: "secondary small-btn seat-answer seat-answer-soon",
      disabled: true,
      title: "Matchmaking is not built yet",
    });
    fill.append(
      el("span", { className: "soon-label", textContent: "Fill" }),
      notReadyNote(),
      soonStamp("seat"),
    );
    const bot = seatAnswer("AI", () => socket?.assignBot(slotName));
    bot.classList.add("plate-br");
    // Held for somebody: the two answers that belong to everybody are the
    // ones the API refuses, so they say why rather than failing silently.
    // Inviting is not one of them — it is what a hold was for.
    if (locked) {
      you.disabled = true;
      bot.disabled = true;
      you.title = "The host is keeping this force for somebody";
      bot.title = you.title;
    }

    const answers = el("div", { className: "seat-answers" }, [
      you,
      invite,
      fill,
      bot,
    ]);
    answers.setAttribute("role", "group");
    answers.setAttribute("aria-label", `Who pilots ${slotName}`);

    return el("div", { className: "seat-fill" }, [
      el("div", { className: "seat-fill-row" }, [
        el("span", {
          className: "hint",
          textContent: locked ? "Held for somebody" : "Open",
        }),
        answers,
      ]),
      inviteForm,
    ]);
  }

  /** One answer, shaped like a segment of the plate the four make between
   * them: the ends carry the notches (`plate-tl`, `plate-br`) and the two in
   * the middle are square. */
  function seatAnswer(label: string, pick: () => void): HTMLButtonElement {
    const button = el("button", {
      type: "button",
      className: "secondary small-btn seat-answer",
      textContent: label,
    });
    button.addEventListener("click", pick);
    return button;
  }

  /**
   * A slot's units, bulleted, each a small painted icon (camo'd in
   * `camoPixels`, or bare sprite grey when null) beside a caption.
   *
   * Pilot name and skill are stubbed "Pilot: n/a" on every unit — the
   * scenario catalog exposes only a flat unit-name list per slot, nothing
   * per-pilot (see the raw-`.mms` note in plan/lobby.md). The label always
   * renders, the same "n/a" convention challenge.ts's pilot tiles already
   * use for the rank stat this game does not have yet, so a future
   * contributor sees exactly what is missing rather than a card that looks
   * incomplete by accident.
   */
  function unitList(
    slotName: string,
    units: readonly ScenarioUnit[],
    camoPixels: Promise<ImageData | null>,
    drawing: number,
  ): HTMLElement {
    const list = el("ul", { className: "force-unit-list" });
    // Kept so a later camo change can repaint these very canvases rather than
    // building a new card - see `repaint`.
    const painters: ((pixels: Promise<ImageData | null>) => void)[] = [];
    units.forEach(({ name: unit, pilot, gunnery, piloting }, index) => {
      const canvas = el("canvas", { className: "unit-icon" });
      canvas.width = 84;
      canvas.height = 72;
      canvas.title = unit;
      // The whole designation, chassis and model both. The model is what
      // separates a Locust LCT-1S from an LCT-1V, and on a screen where the
      // point is to size a force up, dropping it hides the difference between
      // the two machines standing next to each other.
      const bv = unitBv(slotName, index);
      const stats = statsFor(unit);
      const figures: string[] = [];
      if (bv !== null) figures.push(`${bv.toLocaleString()} BV`);
      if (stats) figures.push(`${tonsText(stats.tons)} t`);
      // The crew, on its own line under the machine. The skills always show
      // when the scenario rates them - they are what BV 2.0 multiplies by, so
      // they are what explains the figure below - and the name joins them only
      // when the file gives one. No "Unnamed" placeholder: a crew with skills
      // and no name is a normal thing for a scenario to write, and inventing a
      // word for it says less than the skills alone.
      const crew = [
        pilot,
        gunnery !== undefined && piloting !== undefined
          ? `${gunnery}/${piloting}`
          : null,
      ]
        .filter(Boolean)
        .join(" · ");
      list.append(
        el("li", { className: "force-unit-row" }, [
          canvas,
          el("span", { className: "force-unit-info" }, [
            el("span", {
              className: "force-unit-designation",
              textContent: unit,
            }),
            ...(crew
              ? [
                  el("span", {
                    className: "hint force-unit-crew",
                    textContent: crew,
                  }),
                ]
              : []),
            el("span", {
              className: "hint force-unit-figures",
              // A dash rather than a zero, and rather than nothing: the row
              // keeps its shape, and "we do not know" is said out loud. Most
              // of the catalog has no battle value anywhere (see
              // content/units.ts).
              textContent: figures.length ? figures.join(" · ") : "—",
            }),
          ]),
        ]),
      );

      const paintUnitIcon = previewModule?.paintUnitIcon;
      if (!paintUnitIcon) return;
      const paint = (pixels: Promise<ImageData | null>): void => {
        void pixels
          .then((p) => paintUnitIcon(unit, p))
          .then((image) => {
            if (drawing !== renderSeq) return;
            canvas.getContext("2d")!.putImageData(image, 0, 0);
          })
          .catch((error: unknown) => {
            // A hex that cannot draw stays empty; the caption still names the
            // unit, so nothing is lost but the picture.
            console.warn("lobby: could not draw", unit, error);
          });
      };
      painters.push(paint);
      paint(camoPixels);
    });
    repaint.set(slotName, () => {
      const pixels = camoForSlot(slotName);
      for (const paint of painters) paint(pixels);
    });
    return list;
  }

  /**
   * What a force adds up to, under its list.
   *
   * Battle value is summed from the units rather than taken from the side's
   * own reported total, so the figure and the rows above it can never
   * disagree. Weight comes from the shared machine table, which does not cover
   * the whole catalog — a total built from an incomplete set is marked with a
   * `+` rather than printed as if it were the whole force.
   */
  function forceTotal(
    slotName: string,
    units: readonly ScenarioUnit[],
  ): HTMLElement {
    const bvs = units.map((_, index) => unitBv(slotName, index));
    const knownBv = bvs.filter((bv): bv is number => bv !== null);
    const totals = forceTotals(units.map((unit) => unit.name));
    const figures: string[] = [];
    if (knownBv.length > 0) {
      const sum = knownBv.reduce((a, b) => a + b, 0);
      const partial = knownBv.length < units.length ? "+" : "";
      figures.push(`${sum.toLocaleString()}${partial} BV`);
    }
    if (totals.tons > 0) {
      const partial = totals.complete ? "" : "+";
      figures.push(`${tonsText(totals.tons)}${partial} t`);
      figures.push(`${costText(totals.cost)}${partial} C-bills`);
    }
    return el("p", {
      className: "force-card-total",
      textContent: figures.length
        ? figures.join(" · ")
        : `${units.length} machine${units.length === 1 ? "" : "s"}`,
    });
  }

  /**
   * One Force: the slot's name, its team, the seat itself (claimed or open),
   * the camo control for whoever's force it is, its units with what each is
   * worth, and what the whole force adds up to.
   *
   * The colour is the side's, not the slot's position: see `colourOf`. Team
   * comes from the scenario when it says — a daily does — and stays "n/a"
   * against the hand-written catalog, which carries no alliance data at all
   * (see `teamPill`).
   */
  function forceCard(
    slot: ScenarioSlot,
    seat: MatchPlayer | undefined,
    drawing: number,
  ): HTMLElement {
    const card = el("div", { className: "force-card" });
    // The side's own colour rather than a hue off the slot's position: yours
    // is blue and the opposition is red, the same two the daily card paints
    // its board in, so a force reads the same on both screens.
    const ranks = ranksOf(slot.name);
    card.style.setProperty("--force-colour", sideCss(ranks.team, ranks.slot));
    if (ranks.team === 0) card.classList.add("force-card-mine");
    card.append(
      el("div", { className: "force-card-head" }, [
        el("strong", { className: "force-card-name", textContent: slot.name }),
        teamControl(slot.name),
      ]),
      ...zoneLine(slot.name),
      el("div", { className: "force-card-seat" }, [
        seat ? seatHolder(seat) : seatActions(slot.name),
        ...camoControl(slot.name, seat),
      ]),
      unitList(slot.name, slot.units, camoForSlot(slot.name), drawing),
      forceTotal(slot.name, slot.units),
    );
    return card;
  }

  /** What the scenario itself says this force's team is, before any lobby
   * had an opinion. A daily's index carries the real number; the catalog
   * mostly carries none, and a scenario with no `team_` line is a
   * free-for-all rather than a scenario missing data. */
  function scenarioTeam(slotName: string): number | null {
    return (
      daily?.sides.find((side) => side.name === slotName)?.team ??
      current?.slots.find((slot) => slot.name === slotName)?.team ??
      null
    );
  }

  /** The team a force will actually fight on: the lobby's answer where
   * there is one, the scenario's otherwise. Mirrors the same `or` the API
   * makes once, at launch, in `lobby_seats_to_players`. */
  function teamNow(slotName: string): number | null {
    const seat = seats.find((s) => s.slot === slotName);
    return seat?.team ?? scenarioTeam(slotName);
  }

  /**
   * The team control: which side this force fights on.
   *
   * A scenario's teams are a starting position, not a verdict — Monte
   * Diablo ships as a four-way free-for-all whose description reads like an
   * alliance, and stock MegaMek has had this control in its own lounge all
   * along. Changing one drops every readiness in the lobby, which is the
   * API's doing and the right call: who is allied with whom is exactly what
   * a player who already pressed Ready should be asked about again.
   *
   * Always rendered and never disabled, the same "show it, let the server
   * decide" posture Kick and Deploy take: only the seat's holder or the
   * lobby's owner may move a seat, and this client cannot reliably know
   * which of those it is.
   *
   * A daily gets a pill instead. Its sides are the challenge, and a control
   * that changed them would make it a different fight to the same score.
   */
  function teamControl(slotName: string): HTMLElement {
    const team = teamNow(slotName);
    if (daily) {
      return el("span", {
        className: "pill force-card-team force-card-team-known",
        textContent: team === null ? "Team: n/a" : `Team ${team}`,
      });
    }

    const select = el("select", { className: "force-card-team-pick" });
    select.setAttribute("aria-label", `Team for ${slotName}`);
    const fromFile = scenarioTeam(slotName);
    select.append(
      el("option", {
        value: "",
        // Naming what it falls back to, rather than "Default": a player
        // choosing this wants to know what they are choosing.
        textContent:
          fromFile === null ? "Own side" : `Scenario (team ${fromFile})`,
        selected: seats.find((s) => s.slot === slotName)?.team == null,
      }),
      ...TEAM_NUMBERS.map((number) =>
        el("option", {
          value: String(number),
          textContent: `Team ${number}`,
          selected: seats.find((s) => s.slot === slotName)?.team === number,
        }),
      ),
    );
    select.addEventListener("change", () => {
      socket?.setTeam(
        slotName,
        select.value === "" ? null : Number(select.value),
      );
    });
    return select;
  }

  /** MegaMek's zone names, said the way a person would say them. */
  const ZONE_WORDS: Record<string, string> = {
    ANY: "anywhere on the board",
    N: "the north edge",
    NE: "the north-east corner",
    E: "the east edge",
    SE: "the south-east corner",
    S: "the south edge",
    SW: "the south-west corner",
    W: "the west edge",
    NW: "the north-west corner",
    EDG: "any edge",
    CTR: "the centre",
  };

  /**
   * Where this force starts, in words.
   *
   * Worth saying out loud rather than leaving to the minimap: a force
   * deploying "anywhere on the board" can land inside somebody else's zone,
   * which is the sort of thing you want to see before you deploy rather than
   * after - Monte Diablo shipped that way and it took playing it to notice.
   */
  function zoneLine(slotName: string): HTMLElement[] {
    const zone = daily
      ? daily.sides.find((side) => side.name === slotName)?.start
      : current?.slots.find((s) => s.name === slotName)?.deploy;
    if (!zone) return [];
    const key = zone.toUpperCase().replace(" (DEEP)", "");
    const words = ZONE_WORDS[key];
    const deep = zone.toUpperCase().includes("(DEEP)") ? ", deep" : "";
    return [
      el("p", {
        className: "hint force-card-zone",
        textContent: `Deploys ${words ?? zone}${deep}`,
      }),
    ];
  }

  /**
   * The camo control for one force: your own to choose, the bot's to reroll,
   * and nothing at all on a seat belonging to somebody else.
   *
   * In the force rather than under it. What a player is picking is what *these
   * machines* are painted in, and the strip at the bottom of the screen was
   * the one thing on it that named no force at all.
   */
  function camoControl(
    slotName: string,
    seat: MatchPlayer | undefined,
  ): HTMLElement[] {
    const mine = daily
      ? daily.sides[0]?.name === slotName
      : seat?.did === session.did;
    if (mine) return [camoOpen.button, camoOpen.dialog];
    if (daily || seat?.control === "bot") {
      const held = botCamoFor(slotName);
      return [held.button, held.dialog];
    }
    return [];
  }

  /**
   * The team a force fights on, for grouping.
   *
   * A daily's index carries the real number. The hand-written catalog carries
   * none, and a scenario's factions default to a free-for-all, so there each
   * force is its own team — which is the honest reading of no data, and lays
   * out exactly as it did before.
   */
  function teamOf(slotName: string): string {
    const team = teamNow(slotName);
    return team === null ? `slot:${slotName}` : `team:${team}`;
  }

  /**
   * Set up the fight the Challenge press asked for: this player in the first
   * seat, the player they pressed it on in the next.
   *
   * Only on the first snapshot with seats in it, which is the first one after
   * a scenario is chosen — a fresh lobby has no slots until then, so a
   * challenge is arranged the moment the scenario is, not before.
   *
   * Both moves are sent and neither is assumed: the API answers with a
   * snapshot of what it did, the same as it does for a seat claimed by hand,
   * and this screen draws that. An invite whose handle does not resolve seats
   * nobody, which is what the invite form does too — see `seatActions`.
   */
  function seatTheChallenged(): void {
    if (!toSeat || !seats.length) return;
    const wanted = toSeat;
    toSeat = null;

    let ours = seats.find((seat) => seat.did === session.did)?.slot ?? null;
    if (!ours) {
      const open = seats.find((seat) => seat.control !== "human");
      if (open) {
        socket?.claimSeat(open.slot);
        ours = open.slot;
      }
    }

    // Theirs is the next seat that is neither ours nor already somebody's.
    // Compared by slot rather than by DID: the seat just claimed is still
    // empty in *this* snapshot - the one that says otherwise has not arrived
    // yet - so a DID test would offer them the seat we are sitting down in.
    //
    // A scenario with one slot has nowhere to put them, and nothing is sent
    // rather than somebody being displaced.
    const theirs = seats.find(
      (seat) =>
        seat.slot !== ours &&
        seat.did !== session.did &&
        seat.control !== "human",
    );
    if (theirs) socket?.inviteHandle(theirs.slot, wanted.replace(/^@/, ""));
  }

  function renderSeats(): void {
    seatList.replaceChildren();
    repaint.clear();
    if (!current) return;
    const drawing = ++renderSeq;
    // One column per team, one row per force inside it. Allies belong
    // together: Bug Company's three pirate lances are one side and read as
    // one, rather than as three opponents spread across the width.
    const columns = new Map<string, HTMLElement>();
    for (const slot of current.slots) {
      const key = teamOf(slot.name);
      let column = columns.get(key);
      if (!column) {
        column = el("div", { className: "force-team" });
        columns.set(key, column);
        seatList.append(column);
      }
      const seat = seats.find((s) => s.slot === slot.name);
      column.append(forceCard(slot, seat, drawing));
    }
    seatList.style.setProperty("--force-teams", String(columns.size));
    updateDeploy();
  }

  /** Who said a line: their handle, or a shortened DID for an account with
   * none to show — the same truncation challenge.ts's pilot tiles use for
   * an actor known only by DID. */
  function speaker(line: ChatLine): string {
    return line.handle ? at(line.handle) : `${line.did.slice(0, 16)}…`;
  }

  /**
   * Appends one line to the scrollback and scrolls to it. Called once per
   * `onChat` — the backlog on connect and every live line after — never
   * re-reading the whole history, since the API never sends one back.
   */
  function appendChat(line: ChatLine): void {
    const who = el("strong", {
      className: "chat-speaker",
      textContent: speaker(line),
    });
    // The name a line is attributed to is the most-pointed-at thing in a
    // lobby, which is the whole reason the card exists.
    // Not focusable: the same four people are named on every line, and the
    // roster is one stop each for a keyboard.
    hover.attach(who, () => pilotFacts(line.did, line.handle), {
      focusable: false,
    });
    chatScroll.append(el("p", { className: "chat-line" }, [who, line.text]));
    chatScroll.scrollTop = chatScroll.scrollHeight;
  }

  /**
   * A locally-generated line, never sent to or received from the server —
   * `onOpen` below is the only caller, the moment this screen's socket
   * actually opens. Styled with `chat-system` so it reads as a notice
   * rather than something a player said, and appended through the same
   * scrollback `appendChat` uses so ordering is free: it lands before the
   * backlog and every live line after it, because it is the first thing
   * `onOpen`/`onChat` ever append.
   */
  function appendSystemLine(text: string): void {
    chatScroll.append(el("p", { className: "chat-line chat-system" }, [text]));
    chatScroll.scrollTop = chatScroll.scrollHeight;
  }

  const chatForm = el("form", { className: "chat-form" }, [
    chatInput,
    el("button", {
      type: "submit",
      className: "secondary small-btn",
      textContent: "Send",
    }),
  ]);
  chatForm.addEventListener("submit", (event) => {
    event.preventDefault();
    // Trimmed and length-checked here the same way the API itself refuses a
    // blank or over-cap line - silently, with no rejection to react to (see
    // Lobbies::say), so there is nothing to show the player beyond simply
    // not sending it.
    const text = chatInput.value.trim();
    if (!text || text.length > CHAT_MAX_CHARS) return;
    socket?.sendChat(text);
    chatInput.value = "";
  });

  // ----- your own camo -----------------------------------------------------
  //
  // The strip challenge.ts shows, reused whole (camo-picker.ts). What a
  // selection means is the one thing that differs — see that module's own
  // doc and this screen's: a claimed lobby seat has no per-seat camo field
  // to carry a launch-time choice on, so a *user-initiated* pick here
  // writes the account's stored default instead, the same write the camo
  // editor's own "Set as default" makes, and the same field
  // `lobby_seats_to_players` reads for every human seat at Deploy time.
  // `userInitiated` is what keeps the strip's own auto-preselect (the
  // account's existing default, or None when there is nothing to choose)
  // from turning into a write nobody asked for just by opening this screen.

  /** Repaint the force the player is taking, and nothing else. */
  function repaintMine(): void {
    const mine = daily
      ? daily.sides[0]?.name
      : seats.find((seat) => seat.did === session.did)?.slot;
    if (mine) repaint.get(mine)?.();
  }

  // ----- who is here --------------------------------------------------------

  /** A watcher's name: their handle, or a shortened DID for an account with
   * none — the same fallback `speaker` uses for a chat line. */
  function watcherName(watcher: Watcher): string {
    return watcher.handle ? at(watcher.handle) : `${watcher.did.slice(0, 16)}…`;
  }

  /**
   * Who somebody is to this lobby, for the hover card's note: the force
   * they hold, or that they are only watching.
   *
   * Read at open time rather than baked into the trigger, so a name in a
   * chat line said ten minutes ago still says where that player ended up.
   */
  function pilotFacts(did: string, handle: string | null): PilotFacts {
    const seat = seats.find((s) => s.did === did);
    const note = seat
      ? `Holds ${seat.slot}${readyDids.includes(did) ? " · ready" : ""}`
      : "Watching";
    return { did, handle, note };
  }

  /**
   * The room, and the arrivals and departures since the last snapshot.
   *
   * Join and leave lines are derived here rather than sent: the API
   * broadcasts a full snapshot to everyone at once, so every client draws
   * the same two lines at the same moment, and nothing about who came and
   * went has to live in the chat backlog a late joiner is replayed.
   *
   * The very first snapshot announces nobody. Arriving to a room of four
   * and being told four people just joined is a lie about when they got
   * here, and it is the one case where the diff is against nothing rather
   * than against a previous state.
   */
  function renderRoster(next: Watcher[]): void {
    const before = here;
    here = next;
    if (before) {
      const wasHere = new Set(before.map((watcher) => watcher.did));
      const isHere = new Set(next.map((watcher) => watcher.did));
      for (const watcher of next) {
        if (!wasHere.has(watcher.did)) {
          appendSystemLine(`${watcherName(watcher)} joined.`);
        }
      }
      for (const watcher of before) {
        if (!isHere.has(watcher.did)) {
          appendSystemLine(`${watcherName(watcher)} left.`);
        }
      }
    }

    rosterCount.textContent =
      next.length === 1 ? "1 here" : `${next.length} here`;
    rosterList.replaceChildren(
      ...next.map((watcher) => {
        const face = el("img", { className: "seat-avatar", alt: "" });
        showAvatar(face, watcher.did);
        const chip = el("span", { className: "lobby-watcher" }, [
          face,
          el("span", {
            className: "lobby-watcher-name",
            textContent: watcherName(watcher),
          }),
        ]);
        hover.attach(chip, () => pilotFacts(watcher.did, watcher.handle));
        return chip;
      }),
    );
  }

  // ----- ready ------------------------------------------------------------
  //
  // Deploy is one player's press, and it starts a fight everyone else is in.
  // Readiness is how the rest say they are in fact ready for it: the API
  // refuses to launch while any seated human other than the caller has not
  // pressed Ready (`Lobbies::waiting_on`), and drops every readiness in the
  // lobby the moment a seat or the scenario changes, since what was agreed
  // to is no longer what is on screen. That matters least in today's fixed
  // catalog fight, where the lineup stops moving early, and most once
  // players pick their own forces — see plan/forces.md.

  /**
   * The players a deploy waits on: every human seat except the lobby's
   * owner, who is never asked — pressing Deploy, which is theirs alone, is
   * their readiness.
   *
   * The owner is the same answer for everyone in the room, which is the
   * point: a joiner watching the panel sees the same "waiting for" line the
   * owner does, rather than one naming the owner for a press that is never
   * coming. Before the socket says who the owner is — and on the daily path,
   * where nobody ever will — it is this session, which is what a fresh open
   * is anyway.
   */
  function otherPlayers(): MatchPlayer[] {
    const neverAsked = ownerDid ?? session.did;
    return seats.filter(
      (seat) =>
        seat.control !== "bot" && seat.did !== null && seat.did !== neverAsked,
    );
  }

  /**
   * Of those, the ones who have not pressed Ready. Mirrors `awaiting_ready`
   * on the API side, where the deployer is skipped for the same reason the
   * owner is skipped here: `deploy_lobby` has already refused anyone but the
   * owner by the time it asks, so the two are the same identity.
   */
  function waitingOn(): MatchPlayer[] {
    return otherPlayers().filter((seat) => !readyDids.includes(seat.did!));
  }

  /** A seated player's name for the waiting line — the same handle-or-
   * shortened-DID fallback `speaker` uses for a chat line. */
  function seatName(seat: MatchPlayer): string {
    return seat.handle ? at(seat.handle) : `${(seat.did ?? "").slice(0, 16)}…`;
  }

  /**
   * The ready control and the line under it, drawn from the seat map and
   * the readiness snapshot together — either can arrive without the other.
   *
   * The button is for a player holding a seat and nobody else: an unseated
   * spectator may set a readiness (the API lets anyone connected), but
   * nothing is ever gated on it, and a control that does nothing is worse
   * than no control. The line is for everyone, seated or not, since what it
   * says is why Deploy is not moving.
   */
  function renderReady(): void {
    // The owner is not asked, so they are not offered the button either —
    // the same rule `otherPlayers` applies to everyone else's readiness,
    // applied to this session's own.
    const seated =
      seats.some((seat) => seat.did === session.did) &&
      session.did !== (ownerDid ?? session.did);
    const iAmReady = readyDids.includes(session.did);
    readyToggle.hidden = !seated;
    readyToggle.textContent = iAmReady ? "Ready ✓" : "Ready";
    readyToggle.classList.toggle("active", iAmReady);
    readyToggle.setAttribute("aria-pressed", String(iAmReady));
    const waiting = waitingOn();
    // Nothing at all when there is nobody to wait for: a fight against the
    // bot alone has no readiness question in it, and saying so would only be
    // a line about an absence.
    //
    // The reader is named "you" rather than by their own handle. The line is
    // the same fact for everybody in the room — the API is waiting on these
    // seats — but "Waiting for @thole.example." read to @thole.example is a
    // sentence about somebody else.
    readyNote.textContent =
      otherPlayers().length === 0
        ? ""
        : waiting.length === 0
          ? "Everyone is ready."
          : `Waiting for ${waiting
              .map((seat) =>
                seat.did === session.did ? "you" : seatName(seat),
              )
              .join(", ")}.`;
  }

  readyToggle.addEventListener("click", () => {
    // Sent, not assumed: the button redraws when the API's own snapshot
    // comes back, the same way a claimed seat only appears once the seat map
    // does.
    socket?.setReady(!readyDids.includes(session.did));
  });

  /**
   * Whether Deploy should be clickable: every one of the current scenario's
   * slots has a claimed seat, and every other seated human has pressed
   * Ready. Mirrors `seats_fill_every_slot` and `awaiting_ready` on the API
   * side for the enabled/disabled state only — the server re-checks both
   * itself before it ever launches anything, so a stale click here still
   * has to be handled as a real failure, not assumed away.
   */
  function updateDeploy(): void {
    deploy.disabled =
      !current ||
      !current.slots.every((slot) => seats.some((s) => s.slot === slot.name)) ||
      waitingOn().length > 0;
  }

  /**
   * What Deploy starts, and the only thing the daily path does differently
   * once the screen is up.
   *
   * A daily has no lobby row to deploy: `POST /api/daily/<slug>` claims the
   * player's one attempt and launches the fight against the bot in the same
   * call, and it is also where a spent attempt is refused (409) and a
   * signed-out caller is turned away (401). Both come back as the server's own
   * message, through the same catch below.
   *
   * Null is "there is nothing to deploy yet" — a fresh open whose id has not
   * arrived — which is what the click handler checks instead of matchId.
   */
  function launch(): Promise<string> | null {
    // What the player was shown, not a fresh set: what the Forces block drew
    // is what the match runs.
    const chosen = Promise.all(
      [...botCamo].map(
        async (entry) => [entry[0], await pickedBlob(entry[1].picker)] as const,
      ),
    );
    // The player's own roll, when they took one. Bytes rather than a record
    // key, because an ephemeral camo has no key - see camo-picker.ts.
    const own = camo.rolled ?? undefined;
    const filled = chosen.then(async (pairs) => {
      const rolls = new Map<string, Blob>();
      for (const [slot, blob] of pairs) if (blob) rolls.set(slot, blob);
      // Every other bot force gets its own colour rather than MegaMek's
      // white - which is also what None means on a bot's picker.
      const plain = (current?.slots ?? [])
        .filter((s) => {
          if (rolls.has(s.name)) return false;
          if (daily) return daily.sides[0]?.name !== s.name;
          const seat = seats.find((x) => x.slot === s.name);
          return seat?.control === "bot";
        })
        .map((s) => s.name);
      for (const [slot, blob] of await Promise.all(
        plain.map(async (slot) => [slot, await soloCamo(slot)] as const),
      )) {
        rolls.set(slot, blob);
      }
      return rolls;
    });
    if (daily) {
      return filled
        .then((all) => startDaily(daily.slug, all, own))
        .then((s) => s.matchId);
    }
    const id = matchId;
    return id ? filled.then((all) => deployLobby(id, all, own)) : null;
  }

  deploy.addEventListener("click", () => {
    const launched = launch();
    if (!launched) return;
    deploy.disabled = true;
    deploy.textContent = "Deploying…";
    launched
      .then((newMatchId) => {
        render(...waitingScreen(session, newMatchId));
      })
      .catch((err: unknown) => {
        status.textContent =
          err instanceof ApiError
            ? err.message
            : "The match could not be deployed.";
        deploy.textContent = "Deploy";
        updateDeploy();
      });
  });

  // Registered before the socket exists so a screen change during the
  // opening fetch — before connectLobby ever runs — still has something
  // harmless to tear down. See dom.ts: render() only promotes this once the
  // node tree it belongs to is actually on the page. `left` is set first so
  // the `close` event this same call causes is not read by onClosed below
  // as the connection having dropped out from under a screen still on
  // display.
  onTeardown(() => {
    left = true;
    socket?.close();
    // A card left open would be a card about somebody from a screen that is
    // no longer on the page.
    hover.close();
  });

  /** At least one live event has arrived, so onClosed below can tell "never
   * connected" (a bad or expired id) apart from "was connected, then
   * wasn't" (a real drop) without the two callbacks having to agree on it
   * separately. */
  let connected = false;

  const callbacks = {
    onOpen() {
      appendSystemLine("You are connected to chat.");
    },
    onOwner(did: string) {
      connected = true;
      ownerDid = did;
      // Arrives before the first seat map, so there is nothing to redraw
      // yet on a fresh connection — but a socket that reconnected into a
      // lobby this screen already has seats for would have.
      renderReady();
      updateDeploy();
    },
    onScenario(path: string) {
      connected = true;
      current = scenarioByPath(path) ?? current;
      scenarioNote.remove();
      renderScenarioList();
      renderSeats();
    },
    onSeats(next: MatchPlayer[]) {
      connected = true;
      const before = seats;
      seats = next;
      renderSeats();
      // After the render, so a roll that lands repaints the card this call
      // just built rather than one already replaced.
      dressNewBots(before, next);
      // Who is seated decides whether this player has a Ready button at all,
      // and who the rest of the room is still waiting for.
      renderReady();
      // Last, and after the render for the reason the roll above is: this
      // sends two messages the API answers with a snapshot of its own, and
      // that snapshot is what draws the arrangement.
      seatTheChallenged();
    },
    onDeployed(newMatchId: string) {
      render(...waitingScreen(session, newMatchId));
    },
    onLocked(slots: string[]) {
      connected = true;
      lockedSlots = slots;
      // A lock shows on an open force's own answers, so the cards are what
      // redraw — there is nowhere else it shows.
      renderSeats();
    },
    onPresence(watchers: Watcher[]) {
      connected = true;
      renderRoster(watchers);
    },
    onReady(dids: string[]) {
      connected = true;
      readyDids = dids;
      renderReady();
      updateDeploy();
    },
    onChat(line: ChatLine) {
      appendChat(line);
    },
    onClosed() {
      if (left) return;
      status.textContent = connected
        ? "The connection to this lobby was lost."
        : "This lobby could not be reached. It may have ended, or the link may be wrong.";
    },
  };

  if (entry.kind === "daily") {
    // Everything this screen shows is already in the bundle, so there is
    // nothing to wait for and nothing to connect to: the scenario is the
    // day's, and both seats are taken before anyone has pressed anything.
    // Deploy is enabled by the same updateDeploy() rule every other path
    // uses — every slot claimed — which these two seats satisfy outright.
    if (daily) {
      current = dailyScenario(daily);
      // The player takes the first faction and the bot takes every other one,
      // however many there are. Not just the second: Bug Company fields three
      // pirate lances against one machine, and seating only two of its four
      // factions left two of them unclaimed - which showed a daily's own
      // forces offering "Take it yourself", and left Deploy disabled forever,
      // because updateDeploy wants every slot claimed.
      // No team override on either: a daily's sides are the challenge, and
      // the scenario file is what says who is allied with whom.
      seats = current.slots.map((slot, index) =>
        index === 0
          ? {
              slot: slot.name,
              control: "human",
              did: session.did,
              handle: session.handle,
              team: null,
            }
          : {
              slot: slot.name,
              control: "bot",
              did: null,
              handle: null,
              team: null,
            },
      );
      renderScenarioList();
      renderSeats();
    } else {
      scenarioNote.textContent = "That challenge is not on this site.";
    }
  } else if (entry.kind === "join") {
    const joinId = entry.matchId;
    matchId = joinId;
    copyLink.disabled = false;
    fetchScenarios()
      .then((catalog) => {
        // The whole catalog, unnarrowed: a joiner is not choosing Duel or
        // Scenario's intent, they are joining whatever scenario is already
        // live here, and the picker still needs some pool to render from if
        // it stays interactive for them.
        pool = catalog;
        socket = connectLobby(joinId, callbacks);
      })
      .catch((error: unknown) => {
        console.warn("lobby: the scenario catalog could not be read", error);
        scenarioNote.textContent =
          error instanceof ApiError
            ? error.message
            : "The lobby could not be reached.";
      });
  } else {
    const mode = entry.mode;
    fetchScenarios()
      .then((catalog) => {
        pool = scenarioPool(catalog, mode);
        const first = pool[0];
        if (!first) {
          scenarioNote.textContent = "No scenarios are available.";
          return;
        }
        return openLobby(first.path).then((id) => {
          matchId = id;
          current = first;
          // Not `connected = true`: this render is from the local, just-
          // opened `first`, not from anything the socket has said yet — the
          // real connectLobby() call is still below. Setting it here would
          // tell onClosed a socket that never got past its handshake had
          // been live, the wrong message for a lobby that could not
          // actually be reached.
          scenarioNote.remove();
          renderScenarioList();
          renderSeats();
          copyLink.disabled = false;
          // The address bar still says "#matches" up to this point — there
          // was no id to navigate to before now. Replaced, not pushed: this
          // is the same visit's lobby getting its real address, not a new
          // visit, and pushing it would let the browser's own Back button
          // try to "re-enter" a history entry for a lobby that was never
          // anywhere else. nav.markShown syncs the router's own idea of what
          // is on screen the same way — see nav.ts's own doc for why
          // matches.ts already called it once, with no id, the moment this
          // screen was rendered.
          window.history.replaceState(
            {},
            "",
            `${window.location.pathname}${window.location.search}#lobby/${encodeURIComponent(id)}`,
          );
          nav.markShown!("lobby", id);
          socket = connectLobby(id, callbacks);
        });
      })
      .catch((error: unknown) => {
        console.warn("lobby: could not be opened", error);
        scenarioNote.textContent =
          error instanceof ApiError
            ? error.message
            : "The lobby could not be opened.";
      });
  }

  const back = el("button", {
    type: "button",
    className: "secondary",
    textContent: "Back",
  });
  back.addEventListener("click", () => {
    // This screen has a real address now — #lobby/<id> once matchId is
    // known, still #matches while a fresh open is waiting on it — so
    // leaving has to fix the address bar, not just swap the screen the way
    // waiting.ts's own Back does for a sub-screen with no address of its
    // own. Replaced rather than pushed, the same reasoning as the
    // replaceState above: Back is not a new visit to Matches. nav.start!()
    // — the same re-derive-and-render not-found.ts's own "Go home" button
    // uses after fixing the address the same way — recomputes shown,
    // shownLobbyId and the masthead marker from the new address instead of
    // this duplicating that bookkeeping by hand. It renders the Matches
    // screen itself, tearing this one down through the same onTeardown
    // above, which is what closes the socket.
    //
    // A daily was not reached from Matches and has no address of its own
    // either, so it goes back to the front page, which is where the card that
    // opened it is.
    const here = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState({}, "", daily ? here : `${here}#matches`);
    void nav.start!();
  });

  // No page head, the same call challenge.ts makes: "Duel" or "Scenario" —
  // whichever card sent the player here — already said what this is, and
  // the card under it says what to do next. The hidden h1 names the page
  // for a screen reader without repeating it on screen.
  // Everything a lobby is with other people in it, in one panel that floats
  // over the forces rather than sitting under them: chat, the link that
  // brings somebody here, and Ready. Fixed, so a long scenario list cannot
  // scroll the conversation off the screen — see .lobby-chat, which puts it
  // back in the flow on a narrow viewport, where a floating panel would
  // cover the thing it is about.
  //
  // None of it exists in a daily: no lobby to invite anyone into, one player
  // and a bot with nobody on the other end to talk to or wait for.
  const chatPanel = el("aside", { className: "lobby-chat" }, [
    el("div", { className: "lobby-chat-head" }, [
      el("strong", { textContent: "Chat" }),
      copyLink,
    ]),
    copyStatus,
    rosterCount,
    rosterList,
    chatScroll,
    chatForm,
    el("div", { className: "row lobby-ready-row" }, [readyToggle, readyNote]),
  ]);

  // No page head and no section heading: "Duel" or "Scenario" — whichever
  // card sent the player here — already said what this is, and every block
  // under it names itself. The hidden h1 names the page for a screen reader
  // without repeating it on screen.
  return [
    el("h1", {
      className: "visually-hidden",
      textContent: daily ? "Daily challenge" : "Lobby",
    }),
    el("section", { className: "card challenge" }, [
      el("div", { className: "choice camo-pick" }, [
        el("strong", { textContent: "Scenario" }),
        // The note is the connecting/opening line, and a daily is neither.
        ...(daily ? [] : [scenarioNote]),
        scenarioList,
      ]),
      el("div", { className: "choice camo-pick" }, [
        el("strong", { textContent: "Forces" }),
        seatNote,
        seatList,
      ]),
      // No camo block of its own any more: the choice is made inside the
      // force it applies to, which is where a player is looking when they
      // wonder what these machines will be painted in.
      el("div", { className: "row" }, [deploy, back]),
      status,
    ]),
    ...(daily ? [] : [chatPanel]),
    // Last, and outside every card: it is positioned against the viewport,
    // so what it sits next to in the tree decides nothing but its stacking.
    hover.card,
    footer(),
  ];
}
